import { LanguageContext } from '@axon-enterprise/i18n-helpers';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';


import type { FlowEngine } from '../../services/flows';
import type { Step } from '../../services/flows/types';
import { chat } from '../../services/llm';
import { getLanguageNameForChatGPT } from '../../utils/languageMapper';
import { logger } from '../../utils/logger';

// Create component logger for LLMStep
const llmStepLogger = logger.withComponent('LLMStep');

interface LLMStepType extends Step {
  type: 'llm';
  prompt: string;
  imageVar?: string;
  storeAs?: string;
  loadingMessage?: string;
  timeout?: number;
  retries?: number;
  next?: string;
  errorNext?: string;
}

interface LLMStepProps {
  step: LLMStepType;
  engine: FlowEngine;
  onMessage: (message: { from: string; text: string; temporary?: boolean; isLoading?: boolean }) => void;
}

const LLMStep: React.FC<LLMStepProps> = ({ step, engine, onMessage }) => {
  const [, setIsLoading] = useState(true);
  const hasExecutedRef = useRef(false);
  const { language } = useContext(LanguageContext);

  const executeLLMCall = useCallback(async () => {
    // Convert language code to human-readable name for ChatGPT
    const languageName = getLanguageNameForChatGPT(language);

    // Show loading indicator with a unique temporary message
    const loadingText = step.loadingMessage ? __(step.loadingMessage) : __('Thinking...');
    onMessage({ from: 'bot', text: loadingText, temporary: true, isLoading: true });

    try {
      // Retrieve image from variables if imageVar is specified
      let imageUrl: string | undefined;
      if (step.imageVar) {
        const flowState = engine.getState();
        imageUrl = flowState.variables[step.imageVar];

        if (!imageUrl) {
          llmStepLogger.warn('Image variable specified but no image found', {
            imageVar: step.imageVar
          });
        }
      }


      // Call LLM service with prompt and language for localized response
      const response = await chat({ message: step.prompt, imageUrl: imageUrl }, { language: languageName });

      setIsLoading(false);

      if (response && response.content) {
        // Add the response as a message
        onMessage({ from: 'bot', text: response.content });

        // Proceed to next step if available
        if (step.next) {
          engine.goToNext(step.next);
        }
      } else {
        // No response - treat as error
        onMessage({ from: 'bot', text: __('Sorry, I didn\'t get a response. Please try again.') });
        engine.handleStepError();
      }
    } catch (error) {
      setIsLoading(false);
      llmStepLogger.error('LLM call failed', error);
      onMessage({ from: 'bot', text: __('Sorry, something went wrong. Please try again.') });
      engine.handleStepError();
    }
  }, [engine, step, onMessage, setIsLoading, language]);

  // Execute LLM call on mount
  useEffect(() => {
    if (!hasExecutedRef.current) {
      hasExecutedRef.current = true;
      executeLLMCall();
    }
  }, [executeLLMCall]);

  // Component is purely orchestration - no visual elements
  return null;
};

export default LLMStep;